import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Icon } from '@iconify/react'

// antd
import { Avatar, message, Typography, Tooltip } from 'antd'

// slices
import { loginState } from '../login/login.slice'
import { usersState } from '../users/users.slice'
import { projectsState } from '../projects/projects.slice'
import {
    mainTaskById,
    selectTaskUpdate,
    taskById,
    tasksState,
    updateComment,
    updateMainTaskComment,
} from './tasks.slice'
import { isTaskMutationSuccess } from './tasks.service'

// components
import MediaViewer from './components/mediaViewer'
import DeletePopUp from '../../components/deletePopUp/deletePopUp'
import { showActionNotification } from '../../components/notification'
import MentionEditor from '../../components/MentionEditor/MentionEditor'

// helpers
import { formatCommentDate } from '../../helpers/formatCommentDate'
import { resolveProfilePictureUrl } from '../../components/MentionEditor/MentionEditorMentionBlot'

// styles
import styles from './comments.section.module.css'

const getContentMediaSrc = (el) => {
    if (!el) return null
    const tag = String(el.tagName || '').toUpperCase()
    if (tag === 'IMG') return el.getAttribute('src')
    if (tag === 'VIDEO') {
        return (
            el.currentSrc ||
            el.getAttribute('src') ||
            el.querySelector('source')?.getAttribute('src') ||
            null
        )
    }
    return null
}

function CommentsSection({
    taskId: taskIdProp,
    taskListKind = 'support',
    mentionData,
    setFilteredMembers,
    setCommentChange,
    commentChange,
    onTaskUpdate,
}) {
    const isMainTask = taskListKind === 'main'
    const { Text } = Typography

    const dispatch = useDispatch()

    const { userProfile } = useSelector(loginState)
    const { selectedProject, currentProjectData } = useSelector(projectsState)

    const { selectedTask, selectedSubTask, formSubTask } =
        useSelector(tasksState)

    const { clientModalUsersListData = [] } = useSelector(usersState)

    const resolveTaskId = useCallback(() => {
        if (taskIdProp) return taskIdProp
        if (formSubTask && selectedSubTask?._id) return selectedSubTask._id
        return (
            selectedTask?.data?._id ||
            selectedTask?.data?.[0]?._id ||
            selectedTask?._id ||
            null
        )
    }, [taskIdProp, formSubTask, selectedSubTask, selectedTask])

    const getCommentAuthorId = useCallback((commentData) => {
        const raw =
            commentData?.userId?._id ||
            commentData?.userId ||
            commentData?.user?._id ||
            commentData?.uId ||
            commentData?.createdBy?._id ||
            commentData?.createdBy ||
            null
        return raw != null ? String(raw) : null
    }, [])

    const isOwnComment = useCallback(
        (commentData) => {
            const profileId =
                userProfile?._id || userProfile?.id || userProfile?.userId
            if (!profileId) return false

            const authorId = getCommentAuthorId(commentData)
            if (authorId && authorId === String(profileId)) return true

            const authorName = String(
                commentData?.username ||
                    commentData?.user?.name ||
                    commentData?.user?.userName ||
                    ''
            )
                .trim()
                .toLowerCase()
            const profileName = String(
                userProfile?.userName || userProfile?.name || ''
            )
                .trim()
                .toLowerCase()
            return Boolean(
                authorName && profileName && authorName === profileName
            )
        },
        [getCommentAuthorId, userProfile]
    )

    const [comment, setComment] = useState()
    const [deleteModal, setDeleteModal] = useState(false)
    const [deleteButtonLoading, setDeleteButtonLoading] = useState(false)
    const [editingCommentId, setEditingCommentId] = useState(null)
    const [editCommentData, setEditCommentData] = useState({
        value: '',
        mentionIds: [],
    })
    const [editCommentLoading, setEditCommentLoading] = useState(false)
    //const [filteredMembersForEdit, setFilteredMembersForEdit] = useState([])
    const [contentMediaViewer, setContentMediaViewer] = useState({
        open: false,
        files: [],
        index: 0,
    })

    const handleCommentContentMediaClick = useCallback((e) => {
        const mediaEl = e.target?.closest?.('img, video')
        if (!mediaEl) return
        // Ignore mention avatars
        if (
            mediaEl.closest(
                `.${styles.mentionPill}, .${styles.mentionPillAvatar}, .mention`
            )
        ) {
            return
        }

        const root = e.currentTarget
        if (!root?.contains?.(mediaEl)) return

        const src = getContentMediaSrc(mediaEl)
        if (!src) return

        e.preventDefault()
        e.stopPropagation()

        const mediaNodes = Array.from(
            root.querySelectorAll('img, video')
        ).filter(
            (el) =>
                !el.closest(
                    `.${styles.mentionPill}, .${styles.mentionPillAvatar}, .mention`
                )
        )
        const files = mediaNodes.map(getContentMediaSrc).filter(Boolean)
        const index = Math.max(0, files.indexOf(src))

        setContentMediaViewer({
            open: true,
            files,
            index,
        })
    }, [])

    // Get filtered members for edit
    useEffect(() => {
        if (clientModalUsersListData?.length > 0) {
            let filterUsers = []
            clientModalUsersListData?.length > 0 &&
                clientModalUsersListData.forEach((member) => {
                    if (member?._id !== userProfile?._id) {
                        filterUsers = [
                            ...filterUsers,
                            {
                                id: member?._id,
                                display: `${member?.name}`,
                                profilePicture: member?.profilePicture,
                                email: member?.email,
                            },
                        ]
                    }
                })
            // setFilteredMembersForEdit(filterUsers)
        } else {
            //setFilteredMembersForEdit([])
        }
    }, [clientModalUsersListData, userProfile?._id])

    useEffect(() => {
        const currentId = resolveTaskId()
        if (!currentId) {
            setCommentChange?.([])
            return
        }

        if (selectedSubTask && formSubTask) {
            if (String(selectedSubTask._id) !== String(currentId)) return
            setCommentChange(
                Array.isArray(selectedSubTask?.comments)
                    ? selectedSubTask.comments
                    : []
            )
            return
        }

        const selectedId = selectedTask?.data?._id || selectedTask?._id
        if (String(selectedId || '') !== String(currentId)) return
        setCommentChange(
            Array.isArray(selectedTask?.data?.comments)
                ? selectedTask.data.comments
                : []
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTask, selectedSubTask, formSubTask, resolveTaskId])

    useEffect(() => {
        setEditingCommentId(null)
        setEditCommentData({ value: '', mentionIds: [] })
        setDeleteModal(false)
    }, [taskIdProp])

    // // Get User List
    // useEffect(() => {
    //     dispatch(resetClientModalUsers())
    //     dispatch(
    //         usersListForClientModal({
    //             skip: 0,
    //             filter: {},
    //             limit: 10,
    //             search: '',
    //         })
    //     )
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [])

    // DELETE COMMENT — support: support/task/add-remove-comments/:taskId
    // development: support/main-task/:taskId/comments
    async function deleteComment() {
        const taskId = resolveTaskId()
        const commentId = comment?._id

        if (!taskId || !commentId) {
            setDeleteButtonLoading(false)
            setDeleteModal(false)
            message.error('Unable to delete this comment.')
            return
        }

        setDeleteButtonLoading(true)
        try {
            const removePayload = {
                id: taskId,
                data: {
                    fieldName: 'remove',
                    commentId,
                },
            }
            const result = await dispatch(
                isMainTask
                    ? updateMainTaskComment(removePayload)
                    : updateComment(removePayload)
            )
            const data = result?.payload?.data
            if (!data) {
                message.error('Something went wrong, try again later.')
                return
            }

            const { message: checkMessage } = data
            const isSuccess = isMainTask
                ? isTaskMutationSuccess(data)
                : Boolean(data?.success) || isTaskMutationSuccess(data)

            if (isSuccess) {
                if (typeof setCommentChange === 'function') {
                    setCommentChange((prev) =>
                        (Array.isArray(prev) ? prev : []).filter(
                            (item) => String(item?._id) !== String(commentId)
                        )
                    )
                }

                showActionNotification({
                    message: 'Comment was removed from',
                    subMessage:
                        selectedTask?.data?.projectId?.name ||
                        selectedTask?.data?.projectId ||
                        'this task',
                    actionButtonText: 'Undo',
                    notificationKey: 'comment-deleted',
                })

                setComment(undefined)
                setDeleteModal(false)

                const refreshResult = await dispatch(
                    isMainTask
                        ? mainTaskById({ _id: taskId })
                        : taskById({ _id: taskId })
                )
                const refreshData = refreshResult?.payload?.data
                if (refreshData && !isMainTask && refreshData?.data?.[0]) {
                    dispatch(selectTaskUpdate(refreshData.data[0]))
                }
                if (onTaskUpdate) {
                    onTaskUpdate()
                }
            } else {
                message.error(
                    checkMessage || 'Something went wrong, try again later.'
                )
            }
        } catch (error) {
            message.error('Failed to delete comment')
        } finally {
            setDeleteButtonLoading(false)
            setDeleteModal(false)
        }
    }

    async function checkUser() {
        if (clientModalUsersListData?.length > 0) {
            let filterUsers = []

            clientModalUsersListData?.length > 0 &&
                clientModalUsersListData.forEach((member) => {
                    if (
                        member?._id !== userProfile?._id &&
                        !mentionData?.mentionDataList?.includes(member._id)
                    ) {
                        filterUsers = [
                            ...filterUsers,
                            {
                                id: member?._id,
                                display: `${member?.name}`,
                                profilePicture: member?.profilePicture,
                                email: member?.email,
                            },
                        ]
                    }
                })

            setFilteredMembers(filterUsers)
        } else {
            setFilteredMembers([])
        }
    }

    useEffect(() => {
        // TEAM MEMBER DATA SET WITH FILTER
        if (currentProjectData) {
            checkUser()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        selectedProject,
        mentionData?.mentionDataList,
        clientModalUsersListData,
    ])
    // HANDLE CHANGE MENTIONS COMMENT

    const escapeHtml = (str) =>
        String(str ?? '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')

    const escapeAttrUrl = (url) => String(url ?? '').replace(/"/g, '&quot;')

    /** API may send mentionId as [{ _id, name, profilePicture }] or legacy string ids */
    const normalizeMentionEntries = (mentionIds) => {
        if (!Array.isArray(mentionIds)) return []
        return mentionIds
            .map((m) => {
                if (m && typeof m === 'object' && (m._id || m.id)) {
                    const id = String(m._id || m.id)
                    const name = (m.name || m.email || '').trim()
                    if (!name) return null
                    return {
                        _id: id,
                        name,
                        profilePicture: m.profilePicture,
                    }
                }
                if (typeof m === 'string' && clientModalUsersListData?.length) {
                    const u = clientModalUsersListData.find(
                        (user) => user._id === m
                    )
                    if (!u) return null
                    return {
                        _id: u._id,
                        name: (u.name || u.email || '').trim(),
                        profilePicture: u.profilePicture,
                    }
                }
                return null
            })
            .filter(Boolean)
    }

    const toMentionIdPayload = (mentions) =>
        (mentions || []).map((m) =>
            m && typeof m === 'object' && m._id ? m._id : m
        )

    const getMentionInitials = (userName) =>
        String(userName || '')
            .split(' ')
            .map((n) => n[0])
            .filter(Boolean)
            .join('')
            .slice(0, 2)
            .toUpperCase()

    // Get avatar color based on user name/email
    const getAvatarColor = (name) => {
        const colors = [
            'rgb(139, 92, 246)', // purple
            'rgb(59, 130, 246)', // light blue
            'rgb(34, 197, 94)', // green
            'rgb(239, 68, 68)', // red
            'rgb(245, 158, 11)', // orange
        ]
        if (!name) return colors[0]
        const hash = name
            .split('')
            .reduce((acc, char) => acc + char.charCodeAt(0), 0)
        return colors[hash % colors.length]
    }

    const getCommentAvatarUrl = (commentData) => {
        const authorId = getCommentAuthorId(commentData)
        const fromComment =
            resolveProfilePictureUrl(commentData?.user?.profilePicture) ||
            resolveProfilePictureUrl(commentData?.userId?.profilePicture) ||
            resolveProfilePictureUrl(commentData?.profilePicture) ||
            resolveProfilePictureUrl(commentData?.createdBy?.profilePicture)

        if (fromComment) return fromComment

        if (authorId && clientModalUsersListData?.length) {
            const matched = clientModalUsersListData.find(
                (u) => String(u?._id) === String(authorId)
            )
            const fromUsers = resolveProfilePictureUrl(matched?.profilePicture)
            if (fromUsers) return fromUsers
        }

        if (isOwnComment(commentData)) {
            return resolveProfilePictureUrl(userProfile?.profilePicture)
        }

        return ''
    }

    const buildMentionPillHtml = (user) => {
        const userName = user?.name || user?.email || ''
        if (!userName) return ''
        const avatarColor = getAvatarColor(userName)
        const initials = escapeHtml(getMentionInitials(userName))
        const safeName = escapeHtml(userName)
        const pic = resolveProfilePictureUrl(user?.profilePicture)
        const innerAvatar = pic
            ? `<img src="${escapeAttrUrl(pic)}" alt="" width="18" height="18" style="width:100%;height:100%;border-radius:50%;object-fit:cover;display:block;" />`
            : initials

        return `<span class="${styles.mentionPill}"><span class="${styles.mentionPillAvatar}" style="background-color:${avatarColor};">${innerAvatar}</span><span class="${styles.mentionPillName}">${safeName}</span></span>`
    }

    // Render @mentions as pill chips (avatar + name, no @); supports Quill embeds + plain text
    const renderContentWithMentions = (content, mentionIds) => {
        if (!content) return ''
        const entries = normalizeMentionEntries(mentionIds)
        if (entries.length === 0) return content

        if (typeof document !== 'undefined') {
            try {
                const div = document.createElement('div')
                div.innerHTML = content
                const mentionNodes = div.querySelectorAll(
                    'span[data-id][data-value]'
                )
                mentionNodes.forEach((node) => {
                    if (!node.querySelector?.('.ql-mention-value')) return
                    const id = node.getAttribute('data-id')
                    const val = node.getAttribute('data-value')
                    const entry =
                        entries.find((e) => e._id === id) ||
                        entries.find((e) => e.name === val)
                    if (!entry) return
                    const wrap = document.createElement('span')
                    wrap.innerHTML = buildMentionPillHtml(entry)
                    const pill = wrap.firstElementChild
                    if (pill && node.parentNode) {
                        node.parentNode.replaceChild(pill, node)
                    }
                })
                let html = div.innerHTML
                const sorted = [...entries].sort(
                    (a, b) => (b.name?.length || 0) - (a.name?.length || 0)
                )
                sorted.forEach((user) => {
                    const name = user.name
                    if (!name) return
                    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
                    const re = new RegExp(
                        `@${escaped}(?=\\s|$|[.,!?;:]|&nbsp;)`,
                        'gi'
                    )
                    html = html.replace(re, () => buildMentionPillHtml(user))
                })
                return html
            } catch {
                // fall through
            }
        }

        let processedContent = content
        const sorted = [...entries].sort(
            (a, b) => (b.name?.length || 0) - (a.name?.length || 0)
        )
        sorted.forEach((user) => {
            const name = user.name
            if (!name) return
            const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
            const re = new RegExp(`@${escaped}(?=\\s|$|[.,!?;:]|&nbsp;)`, 'gi')
            processedContent = processedContent.replace(re, () =>
                buildMentionPillHtml(user)
            )
        })
        return processedContent
    }

    // Handle edit comment
    /*  const handleEditComment = (comment) => {
        setEditingCommentId(comment?._id)
        setEditCommentData({
            value: comment?.content || '',
            mentionIds: comment?.mentionId || [],
        })
    } */

    // Handle cancel edit
    const handleCancelEdit = () => {
        setEditingCommentId(null)
        setEditCommentData({
            value: '',
            mentionIds: [],
        })
    }

    // Handle save edited comment
    const handleSaveEdit = async () => {
        if (!editingCommentId) return

        setEditCommentLoading(true)
        try {
            const taskId = resolveTaskId()
            if (!taskId) {
                message.error('Unable to update this comment.')
                return
            }
            const updatePayload = {
                id: taskId,
                data: {
                    fieldName: 'update',
                    commentId: editingCommentId,
                    comment: {
                        content: editCommentData.value,
                        mentionId: toMentionIdPayload(
                            editCommentData.mentionIds
                        ),
                    },
                },
            }
            const result = await dispatch(
                isMainTask
                    ? updateMainTaskComment(updatePayload)
                    : updateComment(updatePayload)
            )

            const data = result?.payload?.data
            if (data) {
                const { success, message: checkMessage } = data
                const isSuccess = isMainTask
                    ? isTaskMutationSuccess(data)
                    : Boolean(success) || isTaskMutationSuccess(data)
                if (isSuccess) {
                    message.success(
                        checkMessage || 'Comment updated successfully'
                    )
                    setEditingCommentId(null)
                    setEditCommentData({
                        value: '',
                        mentionIds: [],
                    })
                    // Refresh task data
                    const refreshResult = await dispatch(
                        isMainTask
                            ? mainTaskById({ _id: taskId })
                            : taskById({ _id: taskId })
                    )
                    const refreshData = refreshResult?.payload?.data
                    if (refreshData && !isMainTask) {
                        dispatch(selectTaskUpdate(refreshData?.data[0]))
                    }
                } else {
                    message.error(checkMessage || 'Failed to update comment')
                }
            }
        } catch (error) {
            message.error('Failed to update comment')
        } finally {
            setEditCommentLoading(false)
        }
    }

    // Handle edit comment change
    const handleEditCommentChange = (html, plainText, mentions) => {
        setEditCommentData({
            value: html,
            mentionIds: mentions,
        })
    }

    return (
        <>
            <div>
                <DeletePopUp
                    previewDelete={deleteModal}
                    onDelete={() => {
                        deleteComment()
                    }}
                    loadingButton={deleteButtonLoading}
                    onCancel={() => {
                        setDeleteButtonLoading(false)
                        setDeleteModal(false)
                    }}
                    modalTitle="Delete Comment"
                    pageKey="Comment"
                />

                {commentChange?.length > 0 ? (
                    <div
                        style={{
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            padding: '8px 0',
                        }}
                    >
                        {commentChange.map((data, index) => {
                            const displayName =
                                data?.username ||
                                data?.user?.name ||
                                data?.user?.userName ||
                                data?.user?.email ||
                                ''
                            const avatarColor = getAvatarColor(
                                displayName || data?.user?.email
                            )
                            const userInitials = displayName
                                ? displayName
                                      .split(' ')
                                      .map((n) => n[0])
                                      .filter(Boolean)
                                      .join('')
                                      .slice(0, 2)
                                      .toUpperCase()
                                : ''
                            const avatarUrl = getCommentAvatarUrl(data)
                            const commentKey = data?._id || `comment-${index}`
                            const commentId = data?._id
                                ? String(data._id)
                                : null
                            const isEditing =
                                commentId &&
                                String(editingCommentId) === commentId
                            const showCommentActions =
                                Boolean(commentId) &&
                                isOwnComment(data) &&
                                !isEditing

                            return (
                                <div
                                    key={commentKey}
                                    className={styles.commentItem}
                                >
                                    {/* Avatar */}
                                    <div style={{ flexShrink: 0 }}>
                                        <Avatar
                                            src={avatarUrl || undefined}
                                            style={{
                                                backgroundColor: avatarColor,
                                                cursor: 'pointer',
                                                height: '28px',
                                                width: '28px',
                                                fontSize: '12px',
                                                fontWeight: 400,
                                            }}
                                        >
                                            {userInitials}
                                        </Avatar>
                                    </div>

                                    {/* Comment Content */}
                                    <div className={styles.commentItemBody}>
                                        {/* Header: Name and Date */}
                                        <div
                                            className={`${styles.commentItemMeta}${
                                                showCommentActions
                                                    ? ` ${styles.commentItemMetaWithActions}`
                                                    : ''
                                            }`}
                                        >
                                            <Text
                                                style={{
                                                    fontSize: '14px',
                                                    fontWeight: 700,
                                                    color: '#1f2937',
                                                }}
                                            >
                                                {data?.username ||
                                                    data?.user?.name ||
                                                    data?.user?.email ||
                                                    'Unknown User'}
                                            </Text>
                                            <Text
                                                style={{
                                                    fontSize: '12px',
                                                    color: '#9ca3af',
                                                    fontWeight: 400,
                                                }}
                                            >
                                                {formatCommentDate(
                                                    data?.createdDate ||
                                                        data?.createdAt
                                                )}
                                            </Text>

                                            {showCommentActions ? (
                                                <div
                                                    className={
                                                        styles.commentActions
                                                    }
                                                >
                                                    <Tooltip
                                                        title="Delete"
                                                        placement="top"
                                                        mouseEnterDelay={0.35}
                                                    >
                                                        <button
                                                            type="button"
                                                            className={`${styles.commentActionBtn} ${styles.commentActionBtnDelete}`}
                                                            aria-label="Delete comment"
                                                            onClick={() => {
                                                                setComment(data)
                                                                setDeleteModal(
                                                                    true
                                                                )
                                                            }}
                                                        >
                                                            <Icon
                                                                icon="mdi:trash-can-outline"
                                                                width={15}
                                                                aria-hidden
                                                            />
                                                        </button>
                                                    </Tooltip>
                                                </div>
                                            ) : null}
                                        </div>

                                        {/* Comment Text with Mentions or Edit View */}
                                        {isEditing ? (
                                            <div
                                                style={{
                                                    marginTop: '8px',
                                                }}
                                            >
                                                <MentionEditor
                                                    projectId={
                                                        selectedProject?._id ||
                                                        currentProjectData?._id
                                                    }
                                                    mentionUserType={
                                                        isMainTask
                                                            ? 'pms'
                                                            : undefined
                                                    }
                                                    excludeUserIds={toMentionIdPayload(
                                                        editCommentData.mentionIds ||
                                                            []
                                                    )}
                                                    handleChangeMentions={
                                                        handleEditCommentChange
                                                    }
                                                    value={
                                                        editCommentData.value
                                                    }
                                                    clearTrigger={false}
                                                    loadingButton={false}
                                                    onClickComment={() => {}}
                                                    disabledButton={false}
                                                    isEditMode={true}
                                                    onCancelEdit={
                                                        handleCancelEdit
                                                    }
                                                    onSaveEdit={handleSaveEdit}
                                                    saveLoading={
                                                        editCommentLoading
                                                    }
                                                    saveDisabled={
                                                        !editCommentData.value ||
                                                        editCommentData.value.trim()
                                                            .length === 0
                                                    }
                                                />
                                            </div>
                                        ) : (
                                            <div
                                                className={
                                                    styles.commentContent
                                                }
                                                style={{
                                                    color: '#374151',
                                                    fontSize: '14px',
                                                    lineHeight: '1.5',
                                                    wordBreak: 'break-word',
                                                }}
                                                onClick={
                                                    handleCommentContentMediaClick
                                                }
                                                dangerouslySetInnerHTML={{
                                                    __html: renderContentWithMentions(
                                                        data?.content,
                                                        data?.mentionId
                                                    ),
                                                }}
                                            />
                                        )}

                                        {Array.isArray(data?.attachments) &&
                                            data.attachments.length > 0 && (
                                                <div
                                                    className={
                                                        styles.commentAttachments
                                                    }
                                                >
                                                    {data.attachments.some(
                                                        (item) => item
                                                    ) && (
                                                        <MediaViewer
                                                            mediaFiles={data.attachments.filter(
                                                                (item) => item
                                                            )}
                                                            handleDelete={() => {
                                                                // Attachments in comments are read-only, no delete functionality
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                ) : (
                    <div className={styles.emptyState} role="status">
                        <div className={styles.emptyIconBadge} aria-hidden>
                            <Icon
                                icon="solar:chat-round-dots-linear"
                                width={22}
                                height={22}
                                className={styles.emptyIcon}
                            />
                        </div>
                        <Text className={styles.emptyTitle}>
                            No comments yet
                        </Text>
                        <Text className={styles.emptySubtitle}>
                            Start the conversation — share updates, questions,
                            or feedback here.
                        </Text>
                    </div>
                )}
            </div>

            <MediaViewer
                mediaFiles={contentMediaViewer.files}
                open={contentMediaViewer.open}
                initialIndex={contentMediaViewer.index}
                showThumbnails={false}
                onOpenChange={(open) =>
                    setContentMediaViewer((prev) => ({
                        ...prev,
                        open,
                        ...(open ? {} : { files: [], index: 0 }),
                    }))
                }
            />
        </>
    )
}

export default CommentsSection
